import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MetricCard } from "@/components/metric-card";
import { DashboardSkeleton } from "@/components/loading-skeleton";
import { Button } from "@/components/ui/button";
import { DateRangePicker } from "@/components/date-range-picker";
import { useDateRange, defaultRange } from "@/lib/use-date-range";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  RefreshCw,
  AlertCircle,
  Heart,
  Phone,
  TrendingUp,
  PoundSterling,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Clock,
  Star,
  ShoppingCart,
  Ban,
  Briefcase,
  Receipt,
  Wallet,
  CreditCard,
} from "lucide-react";

interface HealthMetric {
  label: string;
  value: number;
  trend: string;
  change: string;
}

interface ServiceMetric {
  label: string;
  value: number | string;
  change: string;
}

interface SubMetric {
  name: string;
  count: number;
  percentage: number;
}

interface HubData {
  health: {
    score: number;
    metrics: HealthMetric[];
  };
  customerService: {
    avgUxScore: number;
    maxScore: number;
    metrics: ServiceMetric[];
  };
  salesConversions: {
    overallPercentage: number;
    subMetrics: SubMetric[];
  };
  financials: {
    revenue: {
      totalInvoiced: number;
      totalPaid: number;
      outstanding: number;
    };
    expenses: {
      total: number;
    };
    cashFlow: {
      bankBalance: number;
    };
    profitMargin: number;
  };
}

function ScoreGauge({ score, max = 1000 }: { score: number; max?: number }) {
  const percentage = (score / max) * 100;
  const getColor = () => {
    if (percentage >= 70) return "text-green-500";
    if (percentage >= 40) return "text-yellow-500";
    return "text-red-500";
  };

  return (
    <div className="flex flex-col items-center gap-2" data-testid="gauge-health-score">
      <div className="relative w-40 h-40">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle
            cx="50" cy="50" r="42"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-muted/30"
          />
          <circle
            cx="50" cy="50" r="42"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeDasharray={`${percentage * 2.64} 264`}
            strokeLinecap="round"
            className={getColor()}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-bold ${getColor()}`}>{score}</span>
          <span className="text-xs text-muted-foreground">/ {max}</span>
        </div>
      </div>
    </div>
  );
}

function TrendIcon({ trend }: { trend: string }) {
  if (trend === "up") return <ArrowUpRight className="h-4 w-4 text-green-500" />;
  if (trend === "down") return <ArrowDownRight className="h-4 w-4 text-red-500" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

const serviceIcons: Record<string, React.ReactNode> = {
  "Total Calls": <Phone className="h-4 w-4" />,
  "Inbound Calls": <PhoneIncoming className="h-4 w-4" />,
  "Outbound Calls": <PhoneOutgoing className="h-4 w-4" />,
  "Missed Calls": <PhoneMissed className="h-4 w-4" />,
  "Avoidable Missed": <PhoneMissed className="h-4 w-4 text-red-500" />,
  "Avg Duration (sec)": <Clock className="h-4 w-4" />,
};

export default function BusinessHub() {
  const { dateRange, setDateRange, from, to, rangeQs } = useDateRange();
  const { data, isLoading, error, refetch, isFetching } = useQuery<HubData>({
    queryKey: ["/api/hub/all", from, to],
    queryFn: async () => {
      const res = await fetch(`/api/hub/all${rangeQs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <DashboardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to load Business Hub data</h3>
            <p className="text-muted-foreground text-center mb-4">
              There was an error fetching metrics from BigQuery.
            </p>
            <Button onClick={() => refetch()} data-testid="button-retry">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { health, customerService, salesConversions, financials } = data;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">Business Hub</h1>
          <p className="text-muted-foreground">
            {from && to ? `${from} → ${to}` : "Business health, customer service, sales & financial overview"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="ghost" size="sm" onClick={() => setDateRange(defaultRange())}>Reset</Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="button-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:row-span-2" data-testid="card-business-health">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-red-500" />
              Business Health Score
            </CardTitle>
            <CardDescription>Composite score from operations, satisfaction, revenue &amp; margin</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-6">
            <ScoreGauge score={health.score} />
            <div className="w-full space-y-3">
              {health.metrics.map((m) => (
                <div key={m.label} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <TrendIcon trend={m.trend} />
                    <span className="text-sm truncate">{m.label}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary" className="text-xs" data-testid={`badge-health-${m.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      {m.value}%
                    </Badge>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{m.change}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2" data-testid="card-customer-service">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-blue-500" />
              Customer Service
            </CardTitle>
            <CardDescription>8x8 call performance &amp; quality metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                <span className="text-2xl font-bold" data-testid="text-ux-score">{customerService.avgUxScore.toFixed(1)}</span>
                <span className="text-muted-foreground">/ {customerService.maxScore}</span>
              </div>
              <Progress value={(customerService.avgUxScore / customerService.maxScore) * 100} className="flex-1 h-2" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {customerService.metrics.map((m) => (
                <div key={m.label} className="p-3 rounded-lg border bg-card" data-testid={`metric-service-${m.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {serviceIcons[m.label] || <Phone className="h-4 w-4" />}
                    <span className="text-xs text-muted-foreground">{m.label}</span>
                  </div>
                  <div className="text-lg font-semibold">{typeof m.value === "number" ? m.value.toLocaleString() : m.value}</div>
                  <div className="text-xs text-muted-foreground">{m.change}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2" data-testid="card-sales-conversions">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Sales Conversions
            </CardTitle>
            <CardDescription>Pipeline conversion rates from Commusoft</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl font-bold" data-testid="text-overall-conversion">{salesConversions.overallPercentage}%</span>
              <span className="text-muted-foreground">overall completion rate</span>
            </div>
            <div className="space-y-4">
              {salesConversions.subMetrics.map((m) => {
                const icon = m.name.includes("Cancellation")
                  ? <Ban className="h-4 w-4 text-red-500" />
                  : m.name.includes("Completed")
                  ? <ShoppingCart className="h-4 w-4 text-green-500" />
                  : <Briefcase className="h-4 w-4 text-blue-500" />;
                return (
                  <div key={m.name} data-testid={`metric-conversion-${m.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {icon}
                        <span className="text-sm">{m.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{m.count}</span>
                        <Badge variant="outline" className="text-xs">{m.percentage}%</Badge>
                      </div>
                    </div>
                    <Progress value={m.percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="card-financials">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PoundSterling className="h-5 w-5 text-emerald-500" />
            Financial Overview
          </CardTitle>
          <CardDescription>Xero financial snapshot</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricCard
              title="Total Invoiced"
              value={`£${financials.revenue.totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle="From Xero invoices (ACCREC)"
              icon={<Receipt className="h-4 w-4" />}
            />
            <MetricCard
              title="Paid"
              value={`£${financials.revenue.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={`${((financials.revenue.totalPaid / financials.revenue.totalInvoiced) * 100).toFixed(0)}% collected`}
              icon={<Wallet className="h-4 w-4" />}
            />
            <MetricCard
              title="Outstanding"
              value={`£${financials.revenue.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle="Awaiting payment"
              icon={<CreditCard className="h-4 w-4" />}
            />
            <MetricCard
              title="Expenses"
              value={`£${financials.expenses.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle="Total recorded expenses"
              icon={<Receipt className="h-4 w-4" />}
            />
            <MetricCard
              title="Profit Margin"
              value={`${financials.profitMargin}%`}
              subtitle={`Bank: £${financials.cashFlow.bankBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={<TrendingUp className="h-4 w-4" />}
              trend={financials.profitMargin >= 20 ? 1 : financials.profitMargin >= 0 ? 0 : -1}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
